'use client';

import { useState, useEffect, useRef } from 'react';
import { Check, Copy, AlertCircle, Wand2 } from 'lucide-react';
import { toast } from 'react-hot-toast';
import Typo from 'typo-js';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { spellCheckerContent } from '../../data/spell-checker-content';

interface Suggestion {
    word: string;
    suggestions: string[];
    offset: number;
    length: number;
}

export default function SpellChecker() {
    const [text, setText] = useState('');
    const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [copied, setCopied] = useState(false);
    const [language, setLanguage] = useState('en-US');
    const [isDictionaryLoaded, setIsDictionaryLoaded] = useState(false);

    const dictionary = useRef<Typo | null>(null);

    /* =========================
       LOAD REAL DICTIONARY
    ========================== */

    useEffect(() => {
        async function loadDictionary() {
            setIsDictionaryLoaded(false);

            try {
                const aff = await fetch(`/dictionaries/${language}.aff`).then(r => r.text());
                const dic = await fetch(`/dictionaries/${language}.dic`).then(r => r.text());

                dictionary.current = new Typo(language, aff, dic);

                setIsDictionaryLoaded(true);
            } catch (error) {
                console.error('Dictionary load error:', error);
                toast.error('Failed to load dictionary.');
            }
        }

        loadDictionary();
    }, [language]);

    /* =========================
       SPELL CHECK
    ========================== */

    const checkSpelling = () => {
        if (!text.trim()) return;

        if (!dictionary.current || !isDictionaryLoaded) {
            toast.error('Dictionary is still loading...');
            return;
        }

        setIsLoading(true);

        const wordRegex = /\b[\w']+\b/g;
        const words: Array<{ word: string; offset: number }> = [];

        let match;
        while ((match = wordRegex.exec(text)) !== null) {
            words.push({
                word: match[0],
                offset: match.index
            });
        }

        const misspelled = words.filter(({ word }) => {
            if (word === word.toUpperCase()) return false;
            if (/\d/.test(word)) return false;
            return !dictionary.current!.check(word);
        });

        const newSuggestions = misspelled.map(({ word, offset }) => ({
            word,
            suggestions: dictionary.current!.suggest(word).slice(0, 5),
            offset,
            length: word.length
        }));

        setSuggestions(newSuggestions);
        setIsLoading(false);
    };

    /* =========================
       AUTO CHECK (DEBOUNCED)
    ========================== */

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (text.trim()) checkSpelling();
            else setSuggestions([]);
        }, 600);

        return () => clearTimeout(timeout);
    }, [text]);

    /* =========================
       REPLACE WORD
    ========================== */

    const replaceWord = (offset: number, length: number, replacement: string) => {
        const updated =
            text.substring(0, offset) +
            replacement +
            text.substring(offset + length);

        setText(updated);
        setSuggestions([]);
    };

    /* =========================
       FIX ALL
    ========================== */

    const applyAllCorrections = () => {
        let updated = text;

        [...suggestions]
            .sort((a, b) => b.offset - a.offset)
            .forEach(s => {
                if (s.suggestions[0]) {
                    updated =
                        updated.substring(0, s.offset) +
                        s.suggestions[0] +
                        updated.substring(s.offset + s.length);
                }
            });

        setText(updated);
        setSuggestions([]);
        toast.success('All corrections applied!');
    };

    /* =========================
       SAFE HIGHLIGHTING
    ========================== */

    const escapeHtml = (unsafe: string) =>
        unsafe
            .replace(/&/g, "&amp;")
            .replace(/</g, "&lt;")
            .replace(/>/g, "&gt;")
            .replace(/"/g, "&quot;")
            .replace(/'/g, "&#039;");

    const highlightText = (): { __html: string } => {
        if (!text) return { __html: '' };

        let safeText = escapeHtml(text);

        const sorted = [...suggestions].sort((a, b) => b.offset - a.offset);

        sorted.forEach(s => {
            const before = safeText.substring(0, s.offset);
            const word = safeText.substring(s.offset, s.offset + s.length);
            const after = safeText.substring(s.offset + s.length);

            safeText = `${before}<mark class="bg-yellow-100 text-red-600 font-medium">${word}</mark>${after}`;
        });

        return { __html: safeText };
    };

    /* =========================
       COPY
    ========================== */

    const copyToClipboard = () => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        toast.success('Copied!');
        setTimeout(() => setCopied(false), 2000);
    };

    const wordCount = text.split(/\s+/).filter(Boolean).length;

    /* =========================
       UI
    ========================== */

    return (
        <div className="max-w-6xl mx-auto p-6">
            <div className="mb-8">
                <ToolPageIntro title="Spell Checker - Free Online Spell Check Tool">
                Check spelling and grammar errors with our free online spell checker. Perfect your writing and avoid embarrassing mistakes.
            </ToolPageIntro>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">

                {/* INPUT */}
                <div>
                    <div className="flex justify-between mb-2">
                        <label className="text-sm font-medium">Language</label>
                        <select
                            value={language}
                            onChange={(e) => setLanguage(e.target.value)}
                            className="border rounded-md px-3 py-1 text-sm"
                        >
                            <option value="en-US">English (US)</option>
                            <option value="en-GB">English (UK)</option>
                        </select>
                    </div>

                    <textarea
                        rows={16}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        spellCheck={false}
                        placeholder="Paste or type your text here..."
                        className="w-full border rounded-md p-4 focus:ring-2 focus:ring-blue-500"
                    />

                    <div className="flex justify-between mt-2 text-sm text-gray-500">
                        <span>{wordCount} words</span>
                        <button onClick={copyToClipboard}>
                            {copied ? <Check className="h-4 w-4 text-green-500" /> : <Copy className="h-4 w-4" />}
                        </button>
                    </div>

                    {suggestions.length > 0 && (
                        <button
                            onClick={applyAllCorrections}
                            className="mt-4 flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700"
                        >
                            <Wand2 className="h-4 w-4 mr-2" />
                            Fix All
                        </button>
                    )}
                </div>

                {/* RESULTS */}
                <div>
                    <h2 className="text-lg font-semibold mb-4">
                        Results ({suggestions.length})
                    </h2>

                    {suggestions.length === 0 ? (
                        <p className="text-green-600">No spelling errors found 🎉</p>
                    ) : (
                        <ul className="space-y-4">
                            {suggestions.map((s, i) => (
                                <li key={i} className="border p-3 rounded-md">
                                    <div className="flex items-center mb-2">
                                        <AlertCircle className="h-4 w-4 text-red-500 mr-2" />
                                        <strong>{s.word}</strong>
                                    </div>

                                    <div className="flex flex-wrap gap-2">
                                        {s.suggestions.map((option, idx) => (
                                            <button
                                                key={idx}
                                                onClick={() =>
                                                    replaceWord(s.offset, s.length, option)
                                                }
                                                className="px-2 py-1 bg-blue-100 text-blue-700 rounded-md text-sm"
                                            >
                                                {option}
                                            </button>
                                        ))}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}

                    {suggestions.length > 0 && (
                        <div className="mt-6 border rounded-md p-4">
                            <h3 className="font-medium mb-2">Preview</h3>
                            <div
                                className="prose max-w-none"
                                dangerouslySetInnerHTML={highlightText()}
                            />
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-12">
                <InformationalContent
                    title={spellCheckerContent.title}
                    description={spellCheckerContent.description}
                    content={spellCheckerContent.content}
                    faqs={spellCheckerContent.faqs}
                    relatedTools={spellCheckerContent.relatedTools}
                />
            </div>
        </div>
    );
}